import { SearchOutlined } from "@ant-design/icons";
import { Col, Input, Row, Typography, Form, Select } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  dynamicSet,
  getUserRoleList,
  getUserRoleListClear,
  getUserRoleListRequest,
  useDynamicSelector,
} from "@src/redux";
import DynamicTreeSelect from "../../common/TreeSelect";

const { Title, Text } = Typography;

const getDeepestOrgId = (
  organizationIdValue: any,
  fallback?: string
): string | undefined => {
  if (organizationIdValue == null) return fallback;

  if (typeof organizationIdValue === "string") {
    return organizationIdValue.length > 0 ? organizationIdValue : fallback;
  }

  let items: any[] = [];
  if (Array.isArray(organizationIdValue)) {
    items = organizationIdValue;
  } else if (typeof organizationIdValue === "object") {
    // antd Form can store `name={["organizationId", index]}` as an object
    // `{0: ..., 1: ...}` instead of an array. Normalize either shape.
    const numericKeys = Object.keys(organizationIdValue)
      .filter((k) => /^\d+$/.test(k))
      .map(Number)
      .sort((a, b) => a - b);
    items = numericKeys.map((k) => organizationIdValue[k]);
  }

  for (let i = items.length - 1; i >= 0; i--) {
    const value = items[i];
    if (typeof value === "string" && value.length > 0) {
      return value;
    }
  }
  return fallback;
};

const RoleFilterForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const type = Form.useWatch("type", form);
  const myOrg = Form.useWatch("my_org", form);
  const organizationIdValue = Form.useWatch("organizationId", form);

  const { filter: savedFilter } = useDynamicSelector("role_filter");
  const { pagination: rolePagination } =
    useDynamicSelector("role_pagination");
  const [searchValue, setSearchValue] = useState(
    savedFilter?.searchString || ""
  );
  const hasInitialized = useRef(false);
  const initialPagination = { pageLimit: 10, pageNumber: 1 };

  const { result: userRoleList } = useSelector(getUserRoleList);
  const { result: userResult } = useSelector(getUserRoleList);

  const [statusOptions] = useState<{ label: string; value: string }[]>([
    { label: "All", value: "all" },
    { label: "User", value: "User" },
    { label: "Doctor", value: "Doctor" },
    { label: "Nurse", value: "Nurse" },
    { label: "Clinical Admin", value: "ClinicalAdmin" },
  ]);

  const effectiveOrgId = getDeepestOrgId(organizationIdValue, myOrg);

  useEffect(() => {
    dispatch(getUserRoleListClear());
  }, [dispatch]);

  useEffect(() => {
    form.setFieldsValue({
      search: savedFilter?.searchString || "",
      type: savedFilter?.type || "all",
      my_org: savedFilter?.organizationId,
    });
    setSearchValue(savedFilter?.searchString || "");
  }, [form, savedFilter]);

  const buildFilters = (searchInput: string, orgId?: string): any => ({
    searchString: searchInput || "",
    type: type === "all" ? undefined : type,
    organizationId: orgId || savedFilter?.organizationId || "",
  });

  const requestList = (
    filters: any,
    pagination: { pageLimit: number; pageNumber: number }
  ) => {
    dispatch(dynamicSet("role_filter", { filter: filters }));
    dispatch(dynamicSet("role_pagination", { pagination }));
    dispatch(
      getUserRoleListRequest({
        filter: filters,
        pagination,
      })
    );
  };

  const triggerSearch = (searchInput: string) => {
    requestList(buildFilters(searchInput, effectiveOrgId), initialPagination);
  };

  useEffect(() => {
    if (hasInitialized.current || !effectiveOrgId) return;
    hasInitialized.current = true;
    const pagination = rolePagination || initialPagination;
    requestList(
      buildFilters(savedFilter?.searchString || "", effectiveOrgId),
      pagination
    );
  }, [rolePagination, effectiveOrgId, savedFilter]);

  useEffect(() => {
    if (!hasInitialized.current || !effectiveOrgId) return;
    requestList(buildFilters(searchValue, effectiveOrgId), initialPagination);
  }, [type, effectiveOrgId]);

  return (
    <div className="filter-container">
      <div>
        <Row className="header-filter" align="middle">
          <Col span={24}>
            <Title level={4} className="client-filters-header-title">
              Filters
            </Title>
          </Col>
        </Row>
        <Form layout="vertical" className="common-filters" form={form}>
          <Col span={24}>
            <Form.Item label="Search" className="filter-label" name="search">
              <Input
                prefix={<SearchOutlined />}
                className="filter-input"
                allowClear
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  // API call ONLY on clear
                  if (e.target.value === "") {
                    form.setFieldsValue({ search: "" });
                    triggerSearch("");
                  }
                }}
                onPressEnter={(e) => {
                  form.setFieldsValue({ search: searchValue });
                  triggerSearch(searchValue);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Types" name="type" className="delete_message">
              <Select defaultValue={"all"}>
                {statusOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Row gutter={24}>
            <DynamicTreeSelect
              form={form}
              col={24}
              mutationType="add"
              isRequired={false}
              allowClear={true}
              key="role_filter_form"
            />
          </Row>
        </Form>
      </div>
      <Row className="footer">
        <Col>
          <Text strong style={{ fontSize: "20px" }}>
            Total
          </Text>
        </Col>
        <Col className="total-value">
          <Text strong style={{ color: "#ffffff", fontSize: "17px" }}>
            {userRoleList?.pagination?.totalCount ||
              userResult?.items?.length ||
              0}
          </Text>
        </Col>
      </Row>
    </div>
  );
};

export default RoleFilterForm;
